/**
 * 貼文 / 世界觀的編輯 Modal。
 * 自牆面 PostCard 抽離 inline 編輯，避免編輯中被其他互動觸發的 updateList 打亂。
 *
 * 用法：
 *   const ref = useRef(null);
 *   <PostEditModal ref={ref} contentType="post" onSaved={handleEditSaved} />
 *   ref.current.show(data);
 *
 * onSaved: () => Promise<string|null>
 *   儲存成功後負責讓畫面反映新內容，並回傳非成功狀態碼（成功請回 null）。
 *   回傳值會取代成功訊息、改跳 StatusModal，因此**不可**直接傳入回傳 Promise
 *   或其他 truthy 值的函式（例如 reset()），否則每次儲存都會誤跳錯誤。
 */
import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Axios from '@/shared/api/axios';
import { processImage, generateBlurredImage } from '@/shared/utils/imageProcessor';
import { uploadVideoToCF } from '@/shared/utils/uploadVideoToCF';
import { reindexCaptionsAfterDelete } from '@/shared/utils/mediaCaptions';
import { extractTags, stripTags, tagsToText } from '@/shared/utils/tags';
import { pastePlainText, pastePlainTextSingleLine, blockNewline, toSingleLine } from '@/shared/utils/contentEditableInput';
import { toBackendDateTimeString } from '@/shared/utils/formatDate';
import { parseBackendDate } from '@/shared/utils/parseBackendDate';
import { POST_IMAGE_SIZES, WORLD_IMAGE_SIZES } from '@/shared/constants/mediaSizes';
import EditMediaForm from './EditMediaForm';
import PostMediaEditor from './PostMediaEditor';
import PostImages from './PostImages';
import PostVisibilityForm from './PostVisibilityForm';
import PostScheduleForm from './PostScheduleForm';
import PostSettingBadges from './PostSettingBadges';
import MediaToolbar from './MediaToolbar';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';

// 世界標題上限，對應後端 VARCHAR(100)；用 code-point 計算以與 DB 字元數一致
const MAX_WORLD_TITLE = 100;

const getWorldMedia = (data) => data.world_info.world_media;

const getInitialState = (data, contentType) => {
  // 後端儲存的 content 已剝除 tags（另存於 post_tags / world_tags），
  // 編輯時以 #tag 文字接回內容尾端，供使用者檢視與增刪，儲存時再重新抽出
  // 內文可能是 null（例如只有 #tag 的貼文，tags 抽出後內文為空），編輯框需要字串，null 視為空內文
  const rawContent = (contentType === 'world'
    ? data.world_info.world_content
    : data.post_info.post_content) ?? '';
  const tags = contentType === 'world'
    ? data.world_info.world_tags
    : data.post_info.post_tags;
  const tagText = tagsToText(tags);
  const content = tagText
    ? (rawContent.trim() ? `${rawContent.replace(/\s+$/, '')}\n${tagText}` : tagText)
    : rawContent;
  const title = contentType === 'world'
    ? data.world_info.world_title
    : '';
  const media = contentType === 'world'
    ? getWorldMedia(data)
    : data.post_info.post_media;
  const oldImages = contentType === 'world' && media.length > 0 ? [media[0]] : media;
  const captions = {};
  oldImages.forEach((item, i) => { captions[i] = item.caption || ''; });
  const visibilityType = (data.post_info && data.post_info.post_visibility_type) ?? data.visibility_type ?? 0;
  const unlockPrice = (data.post_info && data.post_info.post_unlock_price) ?? data.unlock_price ?? null;
  // 只有仍是排程狀態（尚未真正發布）的貼文才允許編輯發布時間；已發布的貼文一律視為未排程、不可改
  const isScheduled = (data.post_info && data.post_info.post_is_scheduled) ?? data.post_is_scheduled ?? false;
  const scheduledAtRaw = isScheduled
    ? ((data.post_info && data.post_info.post_created_at) || data.post_created_at)
    : null;
  const scheduledAtMs = scheduledAtRaw ? parseBackendDate(scheduledAtRaw) : null;
  const scheduledAt = scheduledAtMs ? new Date(scheduledAtMs) : null;
  return { content, title, oldImages, captions, visibilityType, unlockPrice, isScheduled, scheduledAt };
};

const PostEditModal = forwardRef(({ contentType = 'post', onSaved }, ref) => {
  const { t } = useTranslation();
  const idolId = useSelector(s => s.user.idolId);

  const [show, setShow] = useState(false);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const contentEditableRef = useRef(null);
  const worldTitleRef = useRef(null);

  // 編輯狀態
  const [oldImages, setOldImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [oldMediaCaptions, setOldMediaCaptions] = useState({});
  const [newMediaCaptions, setNewMediaCaptions] = useState({});
  const [changedMediaCaptions, setChangedMediaCaptions] = useState(new Set());
  const [visibilityType, setVisibilityType] = useState(0);
  const [unlockPrice, setUnlockPrice] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null);

  // 原始值，用來判斷是否有變更
  const [originalContent, setOriginalContent] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalVisibilityType, setOriginalVisibilityType] = useState(0);
  const [originalUnlockPrice, setOriginalUnlockPrice] = useState(null);
  const [originalScheduledAt, setOriginalScheduledAt] = useState(null);
  const [contentChanged, setContentChanged] = useState(false);
  const [titleChanged, setTitleChanged] = useState(false);
  const [isContentTyping, setIsContentTyping] = useState(false);
  const [isTitleTyping, setIsTitleTyping] = useState(false);
  const [titleError, setTitleError] = useState(false);

  // Sub-view 切換：'main' | 'visibility' | 'schedule' | 'caption'
  const [view, setView] = useState('main');
  const [captionMedia, setCaptionMedia] = useState(null);
  // sub-view 的確認/儲存按鈕統一放 Modal.Footer，透過 ref 呼叫 form 暴露的 confirm()/save()，
  // 同一時間只會有一個 sub-view 掛載，共用一個 canConfirm 旗標即可
  const [subViewCanConfirm, setSubViewCanConfirm] = useState(false);
  const visibilityFormRef = useRef(null);
  const scheduleFormRef = useRef(null);
  const captionFormRef = useRef(null);

  // MessageModal refs（過場性訊息，維持獨立 modal）
  const modalSize = useRef(null);
  const modalType = useRef(null);
  const modalThumbnailError = useRef(null);
  const modalImageProcessError = useRef(null);
  const modalPostSuccess = useRef(null);

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [statusCode, setStatusCode] = useState('');

  useImperativeHandle(ref, () => ({
    show: (postData) => {
      if (!postData) return;
      const init = getInitialState(postData, contentType);
      setData(postData);
      setOldImages(init.oldImages);
      setNewImages([]);
      setDeletedImageIds([]);
      setOldMediaCaptions(init.captions);
      setNewMediaCaptions({});
      setChangedMediaCaptions(new Set());
      setVisibilityType(init.visibilityType);
      setUnlockPrice(init.unlockPrice);
      setIsScheduled(init.isScheduled);
      setScheduledAt(init.scheduledAt);
      setOriginalContent(init.content);
      setOriginalTitle(init.title);
      setOriginalVisibilityType(init.visibilityType);
      setOriginalUnlockPrice(init.unlockPrice);
      setOriginalScheduledAt(init.scheduledAt);
      setContentChanged(false);
      setTitleChanged(false);
      setIsContentTyping(init.content.trim() !== '');
      setIsTitleTyping(init.title.trim() !== '');
      setTitleError(false);
      setView('main');
      setCaptionMedia(null);
      setShow(true);
    },
    hide: () => setShow(false),
  }));

  // modal 開啟後把原始內容寫進 contentEditable（contentEditable 不適合靠 children 維持）
  useEffect(() => {
    if (!show) return;
    if (contentEditableRef.current) {
      contentEditableRef.current.innerText = originalContent || '';
    }
    if (contentType === 'world' && worldTitleRef.current) {
      worldTitleRef.current.innerText = originalTitle || '';
    }
  }, [show, originalContent, originalTitle, contentType]);

  // 只在切換 sub-view 時重建，避免每次 render 都產生新物件參照，
  // 導致 PostVisibilityForm/PostScheduleForm 的同步 effect 被誤觸發，把使用者正在輸入的值打回原值
  const visibilityInitialValues = useMemo(() => ({ visibilityType, unlockPrice }), [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const scheduleInitialValues = useMemo(() => ({ scheduledAt }), [view]); // eslint-disable-line react-hooks/exhaustive-deps

  // 發布時間是否被更動（null 與 Date 混用，統一以毫秒比較）；
  // 同時決定 hasChanges 與儲存時要不要送 post_created_at
  const scheduleChanged = useMemo(
    () => (scheduledAt?.getTime() ?? null) !== (originalScheduledAt?.getTime() ?? null),
    [scheduledAt, originalScheduledAt],
  );

  const hasChanges = useMemo(() => {
    if (contentType === 'world' && titleChanged) return true;
    if (contentChanged) return true;
    if (newImages.length > 0 || deletedImageIds.length > 0) return true;
    if (changedMediaCaptions.size > 0) return true;
    if (visibilityType !== originalVisibilityType || unlockPrice !== originalUnlockPrice) return true;
    if (scheduleChanged) return true;
    return false;
  }, [
    contentType, titleChanged, contentChanged, newImages.length, deletedImageIds.length,
    changedMediaCaptions.size, visibilityType, originalVisibilityType, unlockPrice, originalUnlockPrice,
    scheduleChanged,
  ]);

  // 與新增流程一致：必須有文字內容，媒體不能取代文字
  const handleContentChange = useCallback(() => {
    const content = contentEditableRef.current?.innerText || '';
    setIsContentTyping(content.trim() !== '');
    // 對齊 handleSave 的 `stripTags(...).replace(/\s+$/, '')`：尾端空白送出時會被砍，
    // 髒檢查也得砍，否則在內文尾端多按兩次 Enter 會亮起儲存鍵、送出的卻是同一份內容。
    // 只裁尾端不用 trim()：內文是多行欄位，前導空白有意義
    setContentChanged(content.replace(/\s+$/, '') !== originalContent.replace(/\s+$/, ''));
  }, [originalContent]);

  const handleTitleChange = useCallback(() => {
    const title = worldTitleRef.current?.innerText || '';
    setIsTitleTyping(title.trim() !== '');
    // 與 handleSave 一致：送出的是 toSingleLine + trim 過的標題，髒檢查也必須比對
    // 同樣處理過的值，否則把標題中間的空白改成換行會亮起儲存鍵、送出的卻是同一個標題
    setTitleChanged(toSingleLine(title).trim() !== toSingleLine(originalTitle).trim());
    if ([...title.trim()].length <= MAX_WORLD_TITLE) setTitleError(false);
  }, [originalTitle]);

  const handleDeleteImage = useCallback((e, imageId, index) => {
    e.stopPropagation();
    if (contentType === 'world') {
      if (imageId !== undefined) {
        setDeletedImageIds(prev => [...prev, imageId]);
        setOldImages([]);
        setOldMediaCaptions(prev => ({ ...prev, 0: '' }));
      } else {
        // 取消新圖後畫面無圖；後端僅在收到新圖時才替換，
        // 被替換頂掉且尚未標記刪除的舊圖須補標記，儲存後才不會殘留
        const originalMedia = getWorldMedia(data);
        if (originalMedia.length > 0 && !deletedImageIds.includes(originalMedia[0].id)) {
          setDeletedImageIds(prev => [...prev, originalMedia[0].id]);
        }
        setNewImages([]);
        setNewMediaCaptions(prev => ({ ...prev, 0: '' }));
      }
      return;
    }
    const isOldMedia = index < oldImages.length;
    if (isOldMedia) {
      const actualIndex = index;
      const mediaToDelete = oldImages[actualIndex];
      setDeletedImageIds(prev => [...prev, mediaToDelete.id]);
      setOldImages(prev => prev.filter((_, i) => i !== actualIndex));
      setOldMediaCaptions(prev => reindexCaptionsAfterDelete(prev, actualIndex));
    } else {
      const actualIndex = index - oldImages.length;
      setNewImages(prev => prev.filter((_, i) => i !== actualIndex));
      setNewMediaCaptions(prev => reindexCaptionsAfterDelete(prev, actualIndex));
    }
  }, [contentType, oldImages, data, deletedImageIds]);

  const handleMediaCaptionChange = useCallback((index, caption, isOld = false) => {
    if (isOld) {
      setOldMediaCaptions(prev => ({ ...prev, [index]: caption }));
      const media = oldImages[index];
      if (media && media.id) {
        setChangedMediaCaptions(prev => new Set([...prev, media.id]));
      }
    } else {
      setNewMediaCaptions(prev => ({ ...prev, [index]: caption }));
    }
  }, [oldImages]);

  const handleEditMedia = useCallback((index) => {
    const isOldMedia = index < oldImages.length;
    const actualIndex = isOldMedia ? index : index - oldImages.length;
    const media = isOldMedia ? oldImages[actualIndex] : newImages[actualIndex];
    const currentCaption = isOldMedia ? oldMediaCaptions[actualIndex] : newMediaCaptions[actualIndex];
    setCaptionMedia({ ...media, index: actualIndex, isOldMedia, initialCaption: currentCaption || '' });
    setSubViewCanConfirm(false);
    setView('caption');
  }, [oldImages, newImages, oldMediaCaptions, newMediaCaptions]);

  const handleSaveMediaCaption = useCallback((mediaIndex, caption) => {
    if (!captionMedia) return;
    if (captionMedia.isOldMedia) {
      handleMediaCaptionChange(mediaIndex, caption, true);
    } else {
      setNewMediaCaptions(prev => ({ ...prev, [mediaIndex]: caption }));
    }
    setView('main');
    setCaptionMedia(null);
  }, [captionMedia, handleMediaCaptionChange]);

  const handleVisibilityConfirm = useCallback((values) => {
    setVisibilityType(values.visibilityType);
    setUnlockPrice(values.unlockPrice);
    setView('main');
  }, []);

  const handleScheduleConfirm = useCallback((values) => {
    setScheduledAt(values.scheduledAt);
    setView('main');
  }, []);

  const handleBackToMain = useCallback(() => {
    setView('main');
    setCaptionMedia(null);
  }, []);

  // 驗證並處理圖片；回傳 media 物件，沒有檔案回傳 null，失敗回傳 'size' | 'type' | 'process'
  // （對應的錯誤 modal 見下方 mediaErrorModals，由呼叫端決定何時顯示）
  const validateMedia = async (file) => {
    if (!file) return null;
    if (!file.type.startsWith('image/')) return 'type';
    if (file.size > 10 * 1024 * 1024) return 'size';
    if (!['image/jpg', 'image/jpeg', 'image/png'].includes(file.type)) return 'type';
    try {
      const results = await processImage(file, {
        sizes: contentType === 'world' ? WORLD_IMAGE_SIZES : POST_IMAGE_SIZES,
      });
      return {
        type: 0,
        file: results.small,
        big_file: results.big,
        url: URL.createObjectURL(results.small),
        big_url: URL.createObjectURL(results.big),
      };
    } catch (error) {
      // 圖片讀取／解碼失敗（例如檔案損毀）
      console.error('Error processing media:', error);
      return 'process';
    }
  };

  const mediaErrorModals = { size: modalSize, type: modalType, process: modalImageProcessError };

  // index 一律於 updater 內取得：updater 需為純函數（不可內嵌其他 setState），
  // 且並發選檔時以呼叫當下的長度計算會導致 caption 錯位。
  // 注意：仰賴 newImages 的 useState 宣告在 newMediaCaptions 之前（updater 依宣告順序執行，index 才保證已賦值）
  const appendNewMedia = useCallback((item) => {
    let appendIndex;
    setNewImages(prev => {
      appendIndex = prev.length;
      return [...prev, item];
    });
    setNewMediaCaptions(prev => ({ ...prev, [appendIndex]: '' }));
  }, []);

  const removeUploadingVideo = useCallback((localId) => {
    let removedIndex = -1;
    setNewImages(prev => {
      const idx = prev.findIndex(item => item.localId === localId);
      if (idx < 0) return prev;
      removedIndex = idx;
      return prev.filter(item => item.localId !== localId);
    });
    setNewMediaCaptions(prev => (removedIndex < 0 ? prev : reindexCaptionsAfterDelete(prev, removedIndex)));
  }, []);

  const startVideoUpload = useCallback(async (file, localId) => {
    try {
      const { uid } = await uploadVideoToCF(
        file,
        () => Axios.post('/post/get-stream-upload-url', { idol_id: idolId }),
      );
      const videoUrl = URL.createObjectURL(file);
      setNewImages(prev => prev.map(item =>
        item.localId === localId
          ? { type: 1, uid, url: videoUrl, localId }
          : item
      ));
    } catch (error) {
      console.error('CF upload failed:', error);
      removeUploadingVideo(localId);
      modalThumbnailError.current?.show();
    }
  }, [idolId, removeUploadingVideo]);

  const uploadMedia = async (e) => {
    // 先 snapshot 成陣列再清 value：避免 async 迴圈中 FileList 被清空導致漏處理；
    // 同時讓使用者下次能再選相同檔案。
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    if (contentType === 'world') {
      const result = await validateMedia(files[0]);
      if (typeof result === 'string') {
        mediaErrorModals[result].current?.show();
        return;
      }
      if (result) {
        setOldImages([]);
        setNewImages([result]);
        setNewMediaCaptions({ 0: '' });
      }
      return;
    }
    // 個別檔案驗證失敗只跳過該檔，不放棄整批；錯誤 modal 每批最多顯示一次，避免疊開
    let errorShown = false;
    const showMediaError = (reason) => {
      if (errorShown) return;
      errorShown = true;
      mediaErrorModals[reason].current?.show();
    };
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('video/')) {
        if (file.size > 200 * 1024 * 1024) { showMediaError('size'); continue; }
        const localId = `${Date.now()}_${i}`;
        appendNewMedia({ type: 1, uploading: true, localId });
        startVideoUpload(file, localId);
      } else {
        const result = await validateMedia(file);
        if (typeof result === 'string') { showMediaError(result); continue; }
        if (result) appendNewMedia(result);
      }
    }
  };

  const handleClose = useCallback(() => {
    setShow(false);
  }, []);

  const handleSave = async () => {
    if (!data) return;
    const fullContent = contentEditableRef.current?.innerText || '';
    // 與 WorldCreate 一致，標題於讀取當下就正規化：驗證與送出必須是同一個值，
    // 否則貼上尾端帶換行的標題會通過長度驗證、卻把換行一起寫進 DB。
    // toSingleLine 收掉 onPaste / onKeyDown 攔不到的入口（拖曳、軟鍵盤換行鍵、
    // 語音聽寫），以及 DB 既有的髒資料 —— 這些路徑最終都會經過這裡
    const title = toSingleLine(worldTitleRef.current?.innerText || '').trim();
    // 與新增流程一致：必須有文字內容
    if (!fullContent.trim()) return;
    if (contentType === 'world') {
      if (!title) return;
      if ([...title].length > MAX_WORLD_TITLE) {
        setTitleError(true);
        return;
      }
    }

    setIsLoading(true);

    try {
      const filterTags = extractTags(fullContent);
      // 尾端 trim：避免剝除接回的 tags 後，殘留的換行/空白在每次編輯循環累積
      const contentWithoutTags = stripTags(fullContent).replace(/\s+$/, '');

      const formData = new FormData();
      formData.append('idol_id', idolId.toString());

      if (contentType === 'world') {
        const worldId = (data.world_info && data.world_info.world_id) || data.world_id;
        formData.append('world_id', worldId.toString());
        formData.append('title', title);
        formData.append('content', contentWithoutTags);
        if (newImages.length > 0) {
          const media = newImages[0];
          if (media.type === 0) {
            formData.append('world_file', media.file);
            formData.append('big_world_file', media.big_file);
            if (newMediaCaptions[0]) {
              formData.append('world_file_caption', newMediaCaptions[0]);
            }
          }
        }
        deletedImageIds.forEach((id, index) => {
          formData.append(`delete_file_ids[${index}]`, id.toString());
        });
      } else {
        formData.append('post_id', ((data.post_info && data.post_info.post_id) || data.id).toString());
        formData.append('content', contentWithoutTags);
        const needsBlurredImages = [1, 2, 4].includes(visibilityType);
        let imageIndex = 0;
        let videoIndex = 0;
        // 組裝 media payload 單獨接錯：generateBlurredImage 是本地 Promise，reject 的是
        // 普通 Error（無 httpErrorCode），交給外層 catch 只會靜默記 log。這裡明確告知
        // 使用者並中止；return 之後外層 finally 仍會解鎖按鈕，編輯內容不會遺失。
        try {
          for (const [index, media] of newImages.entries()) {
            if (media.type === 0) {
              formData.append(`post_files[${imageIndex}]`, media.file);
              formData.append(`big_post_files[${imageIndex}]`, media.big_file);
              if (needsBlurredImages) {
                const blur = await generateBlurredImage(media.file);
                formData.append(`blur_post_files[${imageIndex}]`, blur);
              }
              formData.append(`image_file_captions[${imageIndex}]`, newMediaCaptions[index] || '');
              imageIndex++;
            } else if (media.type === 1) {
              if (!media.uid) continue;
              formData.append(`video_uids[${videoIndex}]`, media.uid);
              formData.append(`video_file_captions[${videoIndex}]`, newMediaCaptions[index] || '');
              videoIndex++;
            }
          }
        } catch (error) {
          console.error('Error building post media payload:', error);
          modalImageProcessError.current?.show();
          return;
        }
        deletedImageIds.forEach((id, index) => {
          formData.append(`delete_file_ids[${index}]`, id.toString());
        });
        formData.append('visibility_type', visibilityType);
        if (unlockPrice !== null && (visibilityType === 2 || visibilityType === 4)) {
          formData.append('unlock_price', unlockPrice);
        }
        // 發布時間沒改就完全不送這個 key（後端才會視為「沒有要更改」）；
        // 已發布的貼文不能改排程，scheduledAt 恆為 null，因此永遠不會進到這個 if，等同不帶 key。
        // 排程中的貼文若改期＝帶新的未來時間；若清除排程＝帶 'now'，讓後端用「自己的」當下時間立即發布。
        // 不能改送前端算好的當下時間：這個時間戳送達後端時，因網路延遲＋前後端時鐘偏差，早已是過去，
        // 可能被後端判定為已逾期而非「立即發布」
        if (scheduleChanged) {
          formData.append('post_created_at', scheduledAt ? toBackendDateTimeString(scheduledAt) : 'now');
        }
      }

      filterTags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });

      const endpoint = contentType === 'world' ? '/world/update' : '/post/update';
      const response = await Axios.post(endpoint, formData);

      if (response.data.status_code === 'SYSTEM_1000') {
        // 主更新已成功，caption 更新失敗時仍關閉並刷新，但以錯誤取代成功訊息
        let captionErrorCode = null;
        if (changedMediaCaptions.size > 0) {
          const mediaUpdates = [];
          changedMediaCaptions.forEach(mediaId => {
            const mediaIndex = oldImages.findIndex(img => img.id === mediaId);
            if (mediaIndex !== -1) {
              mediaUpdates.push({ media_id: mediaId, caption: oldMediaCaptions[mediaIndex] || '' });
            }
          });
          if (mediaUpdates.length > 0) {
            try {
              const captionRes = await Axios.post('/post/update-media-caption', {
                idol_id: idolId,
                media: mediaUpdates,
              });
              if (captionRes.data.status_code !== 'SYSTEM_1000') {
                captionErrorCode = captionRes.data.status_code;
              }
            } catch (error) {
              if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                captionErrorCode = error.httpErrorCode;
              }
            }
          }
        }
        setShow(false);
        // onSaved 負責讓畫面反映新內容，其失敗碼與 caption 一樣「以錯誤取代成功訊息」，
        // 避免同時跳出「儲存成功」與錯誤 modal，讓使用者誤以為儲存失敗而重試
        const savedErrorCode = onSaved ? await onSaved() : null;
        const errorCode = captionErrorCode || savedErrorCode;
        if (errorCode) {
          setStatusCode(errorCode);
          statusModalRef.current?.show();
        } else {
          modalPostSuccess.current?.show();
        }
      } else {
        setStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Error saving: ', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const headerTitle =
    view === 'visibility' ? t('post_visibility_settings')
    : view === 'schedule' ? t('schedule_post')
    : view === 'caption' ? t('edit')
    : t('edit');

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        scrollable
        // 排程 sub-view 內含 DatePicker，flatpickr 的日曆掛在 document.body（modal 之外），
        // Modal 預設的焦點鎖會把日曆內時分／年份輸入框、月份 select 的焦點搶回來，導致完全無法輸入
        enforceFocus={false}
      >
        <Modal.Header className="border-0 pb-0 align-items-center" closeButton>
          {view !== 'main' && (
            <button
              type="button"
              className="btn btn-dark rounded-circle p-0 me-3 d-inline-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px' }}
              onClick={handleBackToMain}
              aria-label={t('back')}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-white fs-14" />
            </button>
          )}
          <Modal.Title className="fw-bold">{headerTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Main view 用 display:none 保留 DOM，避免切回時 contentEditable 內容遺失 */}
          <div style={{ display: view === 'main' ? 'block' : 'none' }}>
            {contentType === 'world' && (
              <>
                <h4
                  ref={worldTitleRef}
                  contentEditable="true"
                  suppressContentEditableWarning={true}
                  onInput={handleTitleChange}
                  onPaste={pastePlainTextSingleLine}
                  onKeyDown={blockNewline}
                  placeholder={!isTitleTyping ? t('enter_title') : ''}
                  style={{
                    outline: 'none',
                    border: titleError ? '1px solid #dc3545' : '1px solid #ced4da',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    minHeight: '40px',
                    cursor: 'text',
                    marginBottom: titleError ? '4px' : '12px',
                  }}
                />
                {titleError && (
                  <small className="text-danger d-block mb-3">{t('in_words', { count: MAX_WORLD_TITLE })}</small>
                )}
                <PostImages
                  contentType="world"
                  oldImages={oldImages}
                  newImages={newImages}
                  editing={true}
                  onDeleteImage={handleDeleteImage}
                  onEditMedia={handleEditMedia}
                />
              </>
            )}

            {contentType === 'post' && (
              <PostSettingBadges
                visibilityType={visibilityType}
                unlockPrice={unlockPrice}
                scheduledAt={scheduledAt}
                onEditVisibility={() => { setSubViewCanConfirm(false); setView('visibility'); }}
                onEditSchedule={() => { setSubViewCanConfirm(false); setView('schedule'); }}
                className="justify-content-end mb-2"
              />
            )}

            <div
              ref={contentEditableRef}
              contentEditable="true"
              className="post-edit"
              onInput={handleContentChange}
              suppressContentEditableWarning={true}
              onPaste={pastePlainText}
              placeholder={!isContentTyping ? t('write_post') : ''}
            />

            {contentType === 'post' && (
              <PostMediaEditor
                images={[...oldImages, ...newImages]}
                onDeleteImage={handleDeleteImage}
                onEditMedia={handleEditMedia}
                mode="edit"
              />
            )}
          </div>

          {view === 'visibility' && (
            <PostVisibilityForm
              ref={visibilityFormRef}
              initialValues={visibilityInitialValues}
              onConfirm={handleVisibilityConfirm}
              onValidityChange={setSubViewCanConfirm}
            />
          )}

          {view === 'schedule' && (
            <PostScheduleForm
              ref={scheduleFormRef}
              initialValues={scheduleInitialValues}
              onConfirm={handleScheduleConfirm}
              onValidityChange={setSubViewCanConfirm}
            />
          )}

          {view === 'caption' && captionMedia && (
            <EditMediaForm
              ref={captionFormRef}
              media={captionMedia}
              initialCaption={captionMedia.initialCaption || ''}
              onSave={handleSaveMediaCaption}
              onValidityChange={setSubViewCanConfirm}
            />
          )}
        </Modal.Body>
        {view === 'main' && (
          <Modal.Footer className="border-0 pt-0 d-block">
            <MediaToolbar
              onFileChange={uploadMedia}
              onVisibilityClick={() => { setSubViewCanConfirm(false); setView('visibility'); }}
              onScheduleClick={() => { setSubViewCanConfirm(false); setView('schedule'); }}
              onSubmit={handleSave}
              onCancel={handleClose}
              canSubmit={
                hasChanges &&
                !newImages.some(img => img.uploading) &&
                isContentTyping &&
                (contentType !== 'world' || isTitleTyping)
              }
              isLoading={isLoading}
              mode="edit"
              submitText={t('save')}
              acceptFiles="image/png,image/jpg,image/jpeg,video/*"
              multiple={contentType !== 'world'}
              showVisibilityButton={contentType !== 'world'}
              showScheduleButton={contentType !== 'world'}
              scheduleDisabled={!isScheduled}
              size="md"
            />
          </Modal.Footer>
        )}
        {view === 'visibility' && (
          <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
            <button className="btn btn-neutral rounded-pill" onClick={handleBackToMain}>
              {t('cancel')}
            </button>
            <button
              className="btn btn-primary rounded-pill"
              onClick={() => visibilityFormRef.current?.confirm()}
              disabled={!subViewCanConfirm}
            >
              {t('confirm')}
            </button>
          </Modal.Footer>
        )}
        {view === 'schedule' && (
          <Modal.Footer className={`border-0 d-flex gap-2 ${scheduledAt ? 'justify-content-between' : 'justify-content-end'}`}>
            {scheduledAt && (
              <button
                type="button"
                className="btn btn-link text-danger px-0"
                onClick={() => scheduleFormRef.current?.clear()}
              >
                {t('post_schedule_clear')}
              </button>
            )}
            <div className="d-flex gap-2">
              <button className="btn btn-neutral rounded-pill" onClick={handleBackToMain}>
                {t('cancel')}
              </button>
              <button
                className="btn btn-primary rounded-pill"
                onClick={() => scheduleFormRef.current?.confirm()}
                disabled={!subViewCanConfirm}
              >
                {t('confirm')}
              </button>
            </div>
          </Modal.Footer>
        )}
        {view === 'caption' && (
          <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
            <button className="btn btn-neutral rounded-pill" onClick={handleBackToMain}>
              {t('cancel')}
            </button>
            <button
              className="btn rounded-pill btn-primaryF"
              onClick={() => captionFormRef.current?.save()}
              disabled={!subViewCanConfirm}
            >
              {t('save')}
            </button>
          </Modal.Footer>
        )}
      </Modal>

      <MessageModal ref={modalSize} status="error">
        <p>{t('post_error2')}</p>
      </MessageModal>
      <MessageModal ref={modalType} status="error">
        <p>{t('post_error3')}</p>
      </MessageModal>
      <MessageModal ref={modalThumbnailError} status="error">
        <p>{t('video_upload_failed')}</p>
      </MessageModal>
      <MessageModal ref={modalImageProcessError} status="error">
        <p>{t('image_process_failed')}</p>
      </MessageModal>
      <MessageModal ref={modalPostSuccess} status="success">
        <p>{contentType === 'world' ? t('world_saved') : t('post_success2')}</p>
      </MessageModal>
      <StatusModal ref={statusModalRef} statusCode={statusCode} />
    </>
  );
});

PostEditModal.displayName = 'PostEditModal';

export default PostEditModal;
